/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * Small helpers with no DOM opinions of their own.
 */

/**
 * Clamps a value between a minimum and maximum (inclusive).
 * @param {number} value - The input value.
 * @param {number} min - Lower bound.
 * @param {number} max - Upper bound.
 * @returns {number}
 */
export const limit = (value, min, max) => {
    const mn = Math.min(min, max);
    const mx = Math.max(min, max);
    if (value < mn) return mn;
    if (value > mx) return mx;
    return value;
};

/**
 * Converts a number to a string with up to `fractionDigits` decimal places,
 * trailing zeros removed.
 * @param {number} n
 * @param {number} [fractionDigits=7]
 * @returns {string}
 */
export const numStr = (n, fractionDigits = 7) => {
    return n.toFixed(fractionDigits).replace(/\.?0+$/, '');
};

/**
 * Returns a single-line text by replacing line breaks in a smart way.
 * @param text - Text with HTML line breaks
 * @return {string}
 * @type {(text: string | null) => string | null}
 */
export const smartStripLineBreaks = (text) => {
    if (!text) {
        return text;
    }

    function replaceBR(s) {
        if (s.match(/^<br *>$/gi)) {
            return '';
        }
        const group1 = s[0];
        const group2 = s[s.length - 1];
        if (group1 !== '-' || group2 === '>') {
            return group2 !== '>' ? ` ${group2 || ''}` : '';
        } else if (group2 === group2.toLowerCase()) {
            return `&shy;${group2}`;
        } else {
            return `-${group2}`;
        }
    }

    return text.replace(/(- *)?<br *\/?>\s*(\w)?/gi, replaceBR);
};

/**
 * Apply style set (props + CSS vars) to an element.
 * @param {Element} el
 * @param {{cssProps?: Record<string,string>, cssVars?: Record<string,string>}} styleSet
 */
export const applyStyleSet = (el, { cssProps = {}, cssVars = {} }) => {
    for (const [k, v] of Object.entries(cssProps)) el.style[k] = v;
    for (const [k, v] of Object.entries(cssVars)) el.style.setProperty(k, v);
};

/**
 * Name a language in its own language, for a subtitle track label.
 * @param {string} code - Two-letter language code.
 * @returns {string}
 */
export const languageDisplayName = (code) => {
    try {
        return (
            new Intl.DisplayNames([code], { type: 'language' }).of(code) ||
            code.toUpperCase()
        );
    } catch (err) {
        return code.toUpperCase();
    }
};
